import React from 'react';
import env from '../../env';

/**
 * CollectionPageSchema Component
 * 
 * Implements Schema.org CollectionPage structured data for SEO.
 * Follows best practices as defined by Schema.org:
 * - https://schema.org/CollectionPage
 * 
 * This schema should be used on collection/category pages that display
 * multiple products (e.g., /clips, /hair-ties, etc.)
 * 
 * @param {Object} props
 * @param {string} props.name - Collection page name/title
 * @param {string} props.url - Collection page URL (relative or absolute)
 * @param {string} props.description - Collection page description
 * @param {string} props.image - Collection page main image URL (optional)
 * @param {Array} props.products - Array of products in the collection
 * @param {string} props.products[].name - Product name
 * @param {string} props.products[].url - Product URL (relative or absolute)
 * @param {string} props.products[].image - Product image URL (optional)
 * @param {Object} props.breadcrumb - Breadcrumb data (optional)
 * 
 * @example
 * <CollectionPageSchema
 *   name="Hair Clips"
 *   url="/clips"
 *   description="Shop our collection of hair clips..."
 *   products={[
 *     { name: "Product 1", url: "/products/product-1", image: "/image1.jpg" },
 *     { name: "Product 2", url: "/products/product-2", image: "/image2.jpg" }
 *   ]}
 * />
 */
export function CollectionPageSchema({ 
  name, 
  url, 
  description, 
  image, 
  products = [], 
  breadcrumb 
}) {
  React.useEffect(() => {
    // Validate required data
    if (!name || !url) {
      return;
    }

    // Remove any existing collection page schema script
    const existingScript = document.querySelector('script[data-collection-page-schema]');
    if (existingScript) {
      existingScript.remove();
    }

    // Build absolute URL
    let absoluteUrl = url;
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      const normalizedUrl = url.startsWith('/') ? url : '/' + url;
      absoluteUrl = env.baseOrigin + normalizedUrl;
    }

    // Build absolute image URL if provided
    let absoluteImageUrl = null;
    if (image) {
      if (image.startsWith('http://') || image.startsWith('https://')) {
        absoluteImageUrl = image;
      } else {
        const normalizedImage = image.startsWith('/') ? image : '/' + image;
        absoluteImageUrl = env.baseOrigin + normalizedImage;
      }
    }

    // Build ItemList from products
    let itemListElement = [];
    if (products && Array.isArray(products) && products.length > 0) {
      itemListElement = products
        .filter(product => product && product.name && product.url)
        .map((product, index) => {
          // Build absolute product URL
          let productUrl = product.url;
          if (!product.url.startsWith('http://') && !product.url.startsWith('https://')) {
            const normalizedProductUrl = product.url.startsWith('/') ? product.url : '/' + product.url;
            productUrl = env.baseOrigin + normalizedProductUrl;
          }

          // Build absolute product image URL if provided
          let productImageUrl = null;
          if (product.image) {
            if (product.image.startsWith('http://') || product.image.startsWith('https://')) {
              productImageUrl = product.image;
            } else {
              const normalizedProductImage = product.image.startsWith('/') ? product.image : '/' + product.image;
              productImageUrl = env.baseOrigin + normalizedProductImage;
            }
          }

          const listItem = {
            "@type": "ListItem",
            "position": index + 1,
            "name": product.name,
            "url": productUrl
          };

          if (productImageUrl) {
            listItem.image = productImageUrl;
          }

          return listItem;
        });
    }

    // Build CollectionPage schema according to Schema.org specification
    const collectionPageSchema = {
      "@context": "https://schema.org/",
      "@type": "CollectionPage",
      "name": name,
      "url": absoluteUrl
    };

    // Add description if provided
    if (description) {
      collectionPageSchema.description = description;
    }

    // Add image if provided
    if (absoluteImageUrl) {
      collectionPageSchema.image = absoluteImageUrl;
    }

    // Add mainEntity with ItemList if products exist
    if (itemListElement.length > 0) {
      collectionPageSchema.mainEntity = {
        "@type": "ItemList",
        "itemListElement": itemListElement,
        "numberOfItems": itemListElement.length
      };
    }

    // Add breadcrumb if provided
    if (breadcrumb && breadcrumb.items && Array.isArray(breadcrumb.items) && breadcrumb.items.length > 0) {
      const breadcrumbList = {
        "@type": "BreadcrumbList",
        "itemListElement": breadcrumb.items
          .filter(item => item && item.title)
          .map((item, index) => {
            const breadcrumbItem = {
              "@type": "ListItem",
              "position": index + 1,
              "name": item.title
            };

            if (item.link) {
              let breadcrumbUrl = item.link;
              if (!item.link.startsWith('http://') && !item.link.startsWith('https://')) {
                const normalizedBreadcrumbUrl = item.link.startsWith('/') ? item.link : '/' + item.link;
                breadcrumbUrl = env.baseOrigin + normalizedBreadcrumbUrl;
              }
              breadcrumbItem.item = breadcrumbUrl;
            }

            return breadcrumbItem;
          })
      };

      if (breadcrumbList.itemListElement.length > 0) {
        collectionPageSchema.breadcrumb = breadcrumbList;
      }
    }

    // Create and inject JSON-LD script
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-collection-page-schema', 'true');
    script.textContent = JSON.stringify(collectionPageSchema, null, 0);
    document.head.appendChild(script);

    // Cleanup function to remove schema when component unmounts
    return () => {
      const scriptToRemove = document.querySelector('script[data-collection-page-schema]');
      if (scriptToRemove) {
        scriptToRemove.remove();
      }
    };
  }, [name, url, description, image, products, breadcrumb]); // Re-run if any prop changes

  // Return null since we're using direct DOM manipulation
  return null;
}
